import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import GetData from "../../../services/getData";
import logPatientChange from "../../../services/patientLogs";
import { buildPatientLogText } from "../../../services/patientLogMessages";
import { Button, IconButton, SegmentedControl } from "../../../components/ui";
import { L, op, pick, renderField } from "./PatientAnketa";
import "./PatientAnketa.css";

/* ------------------------------------------------------------------ */
/*  Упрощённая анкета (template_id = 5, path = anketa-simple)          */
/*  Схема построена по печатному образцу «Анкета» (ankena/Анкета.docx).*/
/*  Стили и рендер полей переиспользуются из PatientAnketa.            */
/* ------------------------------------------------------------------ */

export const ANKETA_SIMPLE_TEMPLATE_ID = 5;

const YES_NO = [
  op("yes", "Да", "Yes"),
  op("no", "Нет", "No"),
];

export const SIMPLE_ANKETA_SECTIONS = [
  {
    id: "personal",
    num: 1,
    title: L("Общие сведения", "General information"),
    fields: [
      { id: "s_fio", kind: "text", label: L("Фамилия имя отчество", "Full name") },
      { id: "s_birth", kind: "text", label: L("Дата рождения", "Date of birth") },
      { id: "s_fill_date", kind: "text", label: L("Дата заполнения", "Date completed") },
    ],
  },
  {
    id: "complaints",
    num: 2,
    title: L("Жалобы и аллергии", "Complaints and allergies"),
    fields: [
      {
        id: "s_complaints",
        kind: "radio",
        label: L(
          "Есть ли у вас жалобы на общее состояние здоровья?",
          "Do you have any complaints about your general health?"
        ),
        options: YES_NO,
      },
      {
        id: "s_complaints_details",
        kind: "textarea",
        label: L("Если да, то что именно вас беспокоит", "If yes, what exactly bothers you"),
        rows: 3,
      },
      {
        id: "s_allergy",
        kind: "radio",
        label: L(
          "Известно ли о наличии у вас аллергий или реакции повышенной чувствительности на лекарственные средства, витамины, болеутоляющие препараты, средства для анестезии (новокаин, лидокаин, ультракаин), продукты питания, латекс, пластыри?",
          "Do you have any known allergies or hypersensitivity reactions to medications, vitamins, painkillers, anesthetics (novocaine, lidocaine, ultracaine), food, latex, or adhesive plasters?"
        ),
        options: YES_NO,
      },
      {
        id: "s_allergy_details",
        kind: "textarea",
        label: L(
          "Если да, то какие аллергии и как они проявляются",
          "If yes, which allergies and how they manifest"
        ),
        rows: 3,
      },
    ],
  },
  {
    id: "infections",
    num: 3,
    title: L("Инфекции и эпидемиологический анамнез", "Infections and epidemiological history"),
    fields: [
      {
        id: "s_acute_infection",
        kind: "radio",
        label: L(
          "Болеете ли вы в настоящий момент острыми инфекционными заболеваниями (ОРЗ, грипп, другие вирусные инфекции)?",
          "Do you currently have any acute infectious disease (ARVI, influenza, other viral infections)?"
        ),
        options: YES_NO,
      },
      {
        id: "s_acute_infection_details",
        kind: "text",
        label: L("Если да, уточните заболевание", "If yes, specify the disease"),
      },
      {
        id: "s_carrier",
        kind: "checks",
        label: L(
          "Болеете ли вы или являетесь носителем",
          "Do you have or carry any of the following"
        ),
        options: [
          op("hepatitis_b", "Вирусный гепатит B", "Viral hepatitis B"),
          op("hepatitis_c", "Вирусный гепатит C", "Viral hepatitis C"),
          op("hepatitis_d", "Вирусный гепатит D", "Viral hepatitis D"),
          op("hepatitis_e", "Вирусный гепатит E", "Viral hepatitis E"),
          op("hiv", "ВИЧ-инфекция", "HIV infection"),
          op("tb", "Туберкулёз", "Tuberculosis"),
          op("syphilis", "Сифилис", "Syphilis"),
          op("none", "Ничего из перечисленного", "None of the above"),
        ],
      },
      {
        id: "s_carrier_details",
        kind: "textarea",
        label: L(
          "Если да, то состоите ли на учёте у врача (где и с какого года)",
          "If yes, are you under medical supervision (where and since when)"
        ),
        rows: 2,
      },
    ],
  },
  {
    id: "neuro",
    num: 4,
    title: L("Неврология и сердечно-сосудистая система", "Neurology and cardiovascular system"),
    fields: [
      {
        id: "s_fainting",
        kind: "radio",
        label: L(
          "Бывали ли у вас обмороки, приступы, эпилептоидные припадки?",
          "Have you ever had fainting, seizures, or epileptoid fits?"
        ),
        options: [
          op("never", "Нет", "No"),
          op("once", "Однократно", "Once"),
          op("often", "Часто", "Often"),
        ],
      },
      {
        id: "s_cvs",
        kind: "radio",
        label: L(
          "Страдаете ли вы сердечно-сосудистыми заболеваниями (ИБС, артериальная гипертензия, мерцательная аритмия, сердечная недостаточность)?",
          "Do you have cardiovascular diseases (coronary heart disease, arterial hypertension, atrial fibrillation, heart failure)?"
        ),
        options: YES_NO,
      },
      {
        id: "s_cvs_details",
        kind: "textarea",
        label: L("Если да, то какими", "If yes, which ones"),
        rows: 2,
      },
      {
        id: "s_diseases",
        kind: "checks",
        label: L("Страдаете ли вы следующими заболеваниями?", "Do you have any of the following diseases?"),
        options: [
          op("muscle", "Мышечные заболевания (миастения, миопатии, миодистрофия)", "Muscle diseases (myasthenia, myopathies, muscular dystrophy)"),
          op("neuro", "Болезни нервной системы (эпилепсия, параличи, депрессии, вспышки агрессии)", "Nervous system diseases (epilepsy, paralysis, depression, outbursts of aggression)"),
          op("anemia", "Анемия", "Anemia"),
          op("hereditary", "Наследственные заболевания", "Hereditary diseases"),
          op("diabetes", "Сахарный диабет", "Diabetes mellitus"),
          op("autoimmune", "Аутоиммунные заболевания", "Autoimmune diseases"),
          op("thyroid", "Заболевания щитовидной железы", "Thyroid diseases"),
          op("none", "Ничего из перечисленного", "None of the above"),
        ],
      },
      {
        id: "s_diseases_details",
        kind: "textarea",
        label: L("Уточнения по заболеваниям", "Details on the diseases"),
        rows: 2,
      },
    ],
  },
  {
    id: "meds",
    num: 5,
    title: L("Лекарства, новообразования, кровь, операции", "Medications, tumors, blood, surgery"),
    fields: [
      {
        id: "s_meds",
        kind: "radio",
        label: L("Принимаете ли вы лекарственные средства?", "Do you take any medications?"),
        options: YES_NO,
      },
      {
        id: "s_meds_details",
        kind: "textarea",
        label: L("Если да, то какие", "If yes, which ones"),
        rows: 2,
      },
      {
        id: "s_tumors",
        kind: "radio",
        label: L("Имеются ли у вас опухолевые образования?", "Do you have any tumor formations?"),
        options: [
          op("none", "Нет", "No"),
          op("benign", "Доброкачественные", "Benign"),
          op("malignant", "Злокачественные", "Malignant"),
        ],
      },
      {
        id: "s_oncologist",
        kind: "radio",
        label: L("Наблюдаетесь ли вы у онколога?", "Are you followed by an oncologist?"),
        options: YES_NO,
      },
      {
        id: "s_bleeding",
        kind: "radio",
        label: L(
          "Часто ли у вас бывают носовые кровотечения, синяки (кровоподтёки), проблемы со свёртываемостью крови?",
          "Do you often have nosebleeds, bruises, or blood clotting problems?"
        ),
        options: YES_NO,
      },
      {
        id: "s_bleeding_details",
        kind: "textarea",
        label: L("Если да, опишите подробнее", "If yes, describe in more detail"),
        rows: 2,
      },
      {
        id: "s_blood_thinners",
        kind: "radio",
        label: L("Принимаете ли вы препараты, разжижающие кровь?", "Do you take blood-thinning medications?"),
        options: YES_NO,
      },
      {
        id: "s_surgery",
        kind: "textarea",
        label: L(
          "Перенесённые операции, травмы, травмы головы (укажите год и какие)",
          "Previous surgeries, injuries, head injuries (specify year and type)"
        ),
        rows: 3,
      },
    ],
  },
  {
    id: "organs",
    num: 6,
    title: L("Внутренние органы", "Internal organs"),
    fields: [
      {
        id: "s_gi",
        kind: "textarea",
        label: L(
          "Имеются ли у вас заболевания органов ЖКТ: гастрит, дуоденит, панкреатит, гепатит, язвенная болезнь, болезни кишечника, поносы, запоры, тошнота",
          "Do you have gastrointestinal diseases: gastritis, duodenitis, pancreatitis, hepatitis, peptic ulcer, bowel diseases, diarrhea, constipation, nausea"
        ),
        rows: 3,
      },
      {
        id: "s_bp",
        kind: "text",
        label: L("Какое у вас нормальное артериальное давление?", "What is your normal blood pressure?"),
        placeholder: L("например, 120/80", "e.g. 120/80"),
      },
      {
        id: "s_urinary",
        kind: "textarea",
        label: L("Болезни мочевыделительной системы", "Urinary system diseases"),
        rows: 2,
      },
      {
        id: "s_urinary_complications",
        kind: "radio",
        label: L("Имелись ли осложнения?", "Have there been any complications?"),
        options: YES_NO,
      },
      {
        id: "s_urinary_complications_details",
        kind: "textarea",
        label: L("Если да, то какие", "If yes, which ones"),
        rows: 2,
      },
      {
        id: "s_vessels",
        kind: "textarea",
        label: L(
          "Болезни сосудов нижних конечностей: варикозная болезнь, тромбоз, тромбофлебит",
          "Lower limb vascular diseases: varicose veins, thrombosis, thrombophlebitis"
        ),
        rows: 2,
      },
      {
        id: "s_endocrine",
        kind: "textarea",
        label: L(
          "Заболевания эндокринной системы: сахарный диабет, заболевания щитовидной железы",
          "Endocrine system diseases: diabetes mellitus, thyroid diseases"
        ),
        rows: 2,
      },
    ],
  },
  {
    id: "female",
    num: 7,
    title: L("Для женщин репродуктивного возраста", "For women of reproductive age"),
    fields: [
      {
        id: "s_pregnancy",
        kind: "radio",
        label: L("Можете ли вы достоверно исключить беременность?", "Can you reliably rule out pregnancy?"),
        options: [
          op("yes", "Да, беременность исключена", "Yes, pregnancy is ruled out"),
          op("no", "Нет, беременность возможна", "No, pregnancy is possible"),
          op("na", "Не применимо", "Not applicable"),
        ],
      },
      {
        id: "s_breastfeeding",
        kind: "radio",
        label: L("Кормите ли вы ребёнка грудью в настоящий момент?", "Are you currently breastfeeding?"),
        options: YES_NO,
      },
      {
        id: "s_cycle",
        kind: "radio",
        label: L(
          "Есть ли у вас нарушения менструального цикла в настоящий момент?",
          "Do you currently have menstrual cycle disorders?"
        ),
        options: YES_NO,
      },
      {
        id: "s_cycle_details",
        kind: "text",
        label: L("Если да, какие", "If yes, which ones"),
      },
      {
        id: "s_contraceptives",
        kind: "radio",
        label: L("Принимаете ли вы гормональные контрацептивы?", "Do you take hormonal contraceptives?"),
        options: YES_NO,
      },
      {
        id: "s_contraceptives_details",
        kind: "text",
        label: L("Если да, какие", "If yes, which ones"),
      },
      {
        id: "s_gyn",
        kind: "textarea",
        label: L("Есть ли у вас гинекологические заболевания, какие?", "Do you have any gynecological diseases, which ones?"),
        rows: 2,
      },
    ],
  },
  {
    id: "weight",
    num: 8,
    title: L("Вес и метаболизм", "Weight and metabolism"),
    fields: [
      {
        id: "s_height_weight",
        kind: "pairs",
        label: L("Рост и вес", "Height and weight"),
        items: [
          { key: "height", label: L("Рост", "Height"), unit: L("см", "cm") },
          { key: "weight", label: L("Вес", "Weight"), unit: L("кг", "kg") },
        ],
      },
      { id: "s_bmi", kind: "text", label: L("ИМТ", "BMI") },
      {
        id: "s_weight_cause",
        kind: "checks",
        label: L("С чем связываете начало проблем с весом", "What do you associate the onset of weight problems with"),
        options: [
          op("puberty", "Период полового созревания", "Puberty"),
          op("sex_start", "Начало половой жизни", "Start of sexual activity"),
          op("contraception", "Приём контрацепции (ОК)", "Use of contraceptives (oral contraceptives)"),
          op("abortion", "После аборта", "After an abortion"),
          op("pregnancy", "Беременность", "Pregnancy"),
          op("breastfeeding", "Грудное вскармливание", "Breastfeeding"),
          op("menopause", "Менопауза", "Menopause"),
          op("trauma", "Травма (например, ДТП)", "Injury (e.g. car accident)"),
          op("surgery", "Операция (например, удаление желчного пузыря)", "Surgery (e.g. gallbladder removal)"),
          op("region", "Смена региона", "Change of region"),
          op("work", "Смена работы с активной на сидячую", "Change from active to sedentary work"),
          op("antidepressants", "Приём антидепрессантов или других препаратов", "Taking antidepressants or other medications"),
          op("other", "Другое", "Other"),
        ],
      },
      {
        id: "s_weight_fluctuations",
        kind: "radio",
        label: L(
          "Были ли волнообразные периоды набора и снижения веса?",
          "Have there been wave-like periods of weight gain and loss?"
        ),
        options: YES_NO,
      },
      {
        id: "s_weight_gain_year",
        kind: "radio",
        label: L("Набирался ли вес в течение последнего года?", "Has weight been gained over the past year?"),
        options: YES_NO,
      },
      {
        id: "s_diets",
        kind: "radio",
        label: L("Пытались ли вы худеть на диете?", "Have you tried dieting to lose weight?"),
        options: YES_NO,
      },
      {
        id: "s_diet_duration",
        kind: "text",
        label: L("Продолжительность диеты", "Duration of the diet"),
      },
      {
        id: "s_diet_kg",
        kind: "text",
        label: L("Сколько удавалось сбрасывать килограммов", "How many kilograms did you manage to lose"),
      },
      {
        id: "s_diet_attempts",
        kind: "text",
        label: L("Всего попыток похудеть было", "Total number of weight loss attempts"),
      },
      {
        id: "s_diet_slowdown",
        kind: "radio",
        label: L(
          "С каждой новой попыткой вес уходил всё медленнее или требовал больше времени?",
          "With each new attempt, did weight come off more slowly or require more time?"
        ),
        options: YES_NO,
      },
      {
        id: "s_fasting",
        kind: "radio",
        label: L("Были ли лечебные (или иные) голодания?", "Have you had therapeutic (or other) fasting?"),
        options: YES_NO,
      },
      {
        id: "s_fasting_details",
        kind: "text",
        label: L("Сколько времени голодали и какой результат", "How long did you fast and what was the result"),
      },
      {
        id: "s_diet_side_effects",
        kind: "textarea",
        label: L("Побочные действия от диет и голоданий", "Side effects of diets and fasting"),
        rows: 2,
      },
      {
        id: "s_weight_meds",
        kind: "checks",
        label: L("Какие лекарства применяли для похудения", "Which medications did you use for weight loss"),
        options: [
          op("meridia", "Меридия", "Meridia"),
          op("lindaxa", "Линдакса", "Lindaxa"),
          op("reduxin", "Редуксин", "Reduxin"),
          op("xenical", "Ксеникал", "Xenical"),
          op("listata", "Листата", "Listata"),
          op("siofor", "Сиофор", "Siofor"),
          op("glucophage", "Глюкофаж", "Glucophage"),
          op("orlistat", "Орлистат", "Orlistat"),
          op("sibutramine", "Сибутрамин", "Sibutramine"),
          op("bad", "БАДы", "Dietary supplements"),
          op("other", "Другое", "Other"),
        ],
      },
      {
        id: "s_weight_meds_kg",
        kind: "text",
        label: L("Сколько удавалось сбрасывать килограммов", "How many kilograms did you manage to lose"),
      },
      {
        id: "s_weight_meds_period",
        kind: "text",
        label: L("За какой срок", "Over what period"),
      },
      {
        id: "s_weight_meds_side_effects",
        kind: "textarea",
        label: L("Побочные действия препаратов", "Side effects of the medications"),
        rows: 2,
      },
      {
        id: "s_physio",
        kind: "checks",
        label: L("Физиотерапевтические методы похудения", "Physiotherapeutic weight loss methods"),
        options: [
          op("mesotherapy", "Мезотерапия", "Mesotherapy"),
          op("lymphatic", "Лимфодренажный / вакуумный массаж", "Lymphatic drainage / vacuum massage"),
          op("lpg", "LPG-терапия", "LPG therapy"),
          op("co2_baths", "Сухоуглекислые ванны", "Dry carbon dioxide baths"),
          op("ultrasound", "УЗ-терапия", "Ultrasound therapy"),
          op("pressotherapy", "Прессотерапия", "Pressotherapy"),
          op("other", "Другое", "Other"),
        ],
      },
      {
        id: "s_physio_kg",
        kind: "text",
        label: L("Отмечалось снижение веса на (кг)", "Weight reduction noted (kg)"),
      },
    ],
  },
  {
    id: "care",
    num: 9,
    title: L("Медицинская помощь и наблюдение", "Medical care and follow-up"),
    fields: [
      {
        id: "s_recent_care",
        kind: "textarea",
        label: L(
          "Обращались ли за медицинской помощью за последние 6 месяцев? Какие сдавали анализы, делали ли УЗИ щитовидной железы, органов брюшной полости, малого таза, сердца?",
          "Have you sought medical care in the last 6 months? Which tests did you take, did you have ultrasound of the thyroid, abdominal organs, pelvis, or heart?"
        ),
        rows: 3,
      },
      {
        id: "s_dietitian",
        kind: "radio",
        label: L("Обращались ли к врачу-диетологу?", "Have you consulted a dietitian?"),
        options: YES_NO,
      },
      {
        id: "s_dietitian_duration",
        kind: "text",
        label: L("Продолжительность процесса похудения", "Duration of the weight loss process"),
      },
      {
        id: "s_dietitian_result",
        kind: "text",
        label: L("На сколько похудели", "How much weight did you lose"),
      },
      {
        id: "s_dietitian_meds",
        kind: "textarea",
        label: L("Дополнительные лекарственные средства", "Additional medications"),
        rows: 2,
      },
    ],
  },
  {
    id: "family",
    num: 10,
    title: L("Семейный анамнез и образ жизни", "Family history and lifestyle"),
    fields: [
      {
        id: "s_family",
        kind: "textarea",
        label: L(
          "У кого из родных по обеим линиям (отец, мать, бабушки, дедушки и другие родственники) есть ожирение и сопутствующие заболевания",
          "Which relatives on both sides (father, mother, grandparents, other relatives) have obesity and related diseases"
        ),
        rows: 3,
      },
      {
        id: "s_warning",
        kind: "textarea",
        label: L("Какие симптомы по здоровью вас настораживают", "Which health symptoms concern you"),
        rows: 2,
      },
      {
        id: "s_psychotherapy",
        kind: "radio",
        label: L(
          "Есть ли потребность обращения к психотерапевту (пищевое поведение)?",
          "Do you feel the need to consult a psychotherapist (eating behavior)?"
        ),
        options: YES_NO,
      },
      {
        id: "s_sport",
        kind: "radio",
        label: L("Занимаетесь ли вы профессиональным спортом?", "Do you do professional sports?"),
        options: YES_NO,
      },
      {
        id: "s_calories",
        kind: "radio",
        label: L("Приходилось ли считать калорийность пищи?", "Have you ever counted calories?"),
        options: YES_NO,
      },
      {
        id: "s_bioimpedance",
        kind: "radio",
        label: L(
          "Проводили ли биоимпедансометрию (определение состава тела — мышцы, жидкость и т.д.)?",
          "Have you had bioimpedance analysis (body composition — muscles, fluid, etc.)?"
        ),
        options: YES_NO,
      },
      { id: "s_meals", kind: "text", label: L("Принимаете пищу ___ раз в день", "Number of meals per day") },
      { id: "s_goal_weight", kind: "text", label: L("Планируете похудеть на (кг)", "Planned weight loss (kg)") },
      { id: "s_goal_period", kind: "text", label: L("За срок", "Within (period)") },
    ],
  },
  {
    id: "goals",
    num: 11,
    title: L("Что бы вы хотели изменить в себе", "What would you like to change"),
    fields: [
      {
        id: "s_goals",
        kind: "checks",
        label: L("Отметьте подходящие варианты", "Mark the applicable options"),
        options: [
          op("skin", "Улучшить качество кожи, повысить эластичность и тургор", "Improve skin quality, elasticity and tone"),
          op("hair_nails", "Укрепить волосы и ногти", "Strengthen hair and nails"),
          op("fatigue", "Уменьшить усталость", "Reduce fatigue"),
          op("memory", "Улучшить память", "Improve memory"),
          op("vision", "Улучшить зрение", "Improve vision"),
          op("performance", "Повысить работоспособность", "Increase performance"),
          op("sleep", "Улучшить сон, снять тревожность", "Improve sleep, relieve anxiety"),
          op("vitamins", "Проверить анализы на витамины, минералы и т.д.", "Check tests for vitamins, minerals, etc."),
        ],
      },
    ],
  },
  {
    id: "confirmation",
    num: 12,
    title: L("Подтверждение пациента", "Patient confirmation"),
    fields: [
      {
        id: "s_confirm_note",
        kind: "note",
        text: L(
          "Я подтверждаю, что предоставленная информация является полной и достоверной насколько мне известно. Я понимаю, что анкета не заменяет консультацию врача, осмотр и экстренную медицинскую помощь. Я обязуюсь сообщать врачу об изменениях состояния, новых препаратах, беременности и аллергических реакциях.",
          "I confirm that the information provided is complete and accurate to the best of my knowledge. I understand that this questionnaire does not replace a doctor's consultation, examination, or emergency medical care. I agree to inform the doctor of any changes in my condition, new medications, pregnancy, and allergic reactions."
        ),
      },
      { id: "s_patient_name", kind: "text", label: L("Фамилия и имя пациента", "Patient full name") },
      {
        id: "s_signature",
        kind: "text",
        label: L("Подпись пациента или законного представителя", "Signature of patient or legal representative"),
      },
      { id: "s_sign_date", kind: "text", label: L("Дата", "Date") },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Компонент страницы                                                 */
/* ------------------------------------------------------------------ */

function PatientAnketaSimple() {
  const navigate = useNavigate();
  const { patientId, docId } = useParams();

  const [lang, setLang] = useState("ru");
  const [values, setValues] = useState({});
  // Снимок последней сохранённой версии — для отмены правок.
  const [savedValues, setSavedValues] = useState({});
  // По умолчанию анкета открывается в режиме просмотра.
  const [isEditing, setIsEditing] = useState(false);
  const [link, setLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handlers = useMemo(
    () => ({
      setVal: (id, val) => setValues((prev) => ({ ...prev, [id]: val })),
      toggleCheck: (id, key) =>
        setValues((prev) => {
          const cur = Array.isArray(prev[id]) ? prev[id] : [];
          return {
            ...prev,
            [id]: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key],
          };
        }),
      setPair: (id, key, val) =>
        setValues((prev) => {
          const cur = prev[id] && typeof prev[id] === "object" ? prev[id] : {};
          return { ...prev, [id]: { ...cur, [key]: val } };
        }),
      setCell: (id, row, col, val) =>
        setValues((prev) => {
          const rows = Array.isArray(prev[id]) ? prev[id].map((r) => ({ ...r })) : [];
          while (rows.length <= row) rows.push({});
          rows[row] = { ...rows[row], [col]: val };
          return { ...prev, [id]: rows };
        }),
    }),
    [],
  );

  const loadDoc = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await GetData({
        module: "patient",
        block: "patient_docs",
        action: "get",
        id_patient: patientId,
        id: docId,
      });

      if (response?.status === "fail" || !response?.result?.id) {
        throw new Error(response?.message || "Документ не найден");
      }

      const doc = response.result;
      if (Number(doc.template_id) !== ANKETA_SIMPLE_TEMPLATE_ID) {
        throw new Error("Документ не является упрощённой анкетой");
      }
      if (doc.data && typeof doc.data === "object" && !Array.isArray(doc.data)) {
        setValues(doc.data);
        setSavedValues(doc.data);
      }
      // Открытие документа всегда начинается с просмотра.
      setIsEditing(false);
      setSaved(false);
    } catch (err) {
      setLoadError(err.message || "Не удалось загрузить анкету");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDoc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, docId]);

  const handleEdit = () => {
    setSaveError("");
    setSaved(false);
    setIsEditing(true);
  };

  // Выход без сохранения: правки отбрасываются, возвращаемся к снимку.
  const handleCancel = () => {
    setValues(savedValues);
    setSaveError("");
    setIsEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    setSaved(false);
    try {
      const response = await GetData({
        module: "patient",
        block: "patient_docs",
        action: "update",
        id_patient: patientId,
        id: docId,
        data: values,
      });
      if (response?.status === "fail") {
        throw new Error(response?.message || "Не удалось сохранить анкету");
      }
      // Фиксируем новую сохранённую версию и возвращаемся в просмотр.
      setSavedValues(values);
      setIsEditing(false);
      setSaved(true);
    } catch (err) {
      // Остаёмся в режиме редактирования, чтобы не потерять введённые данные.
      setSaveError(err.message || "Не удалось сохранить анкету");
    } finally {
      setSaving(false);
    }
  };

  // Печать документа: скрываем всё, кроме контента вкладки (как в остальных
  // документах медкарты), и вызываем системную печать.
  const handlePrint = () => {
    if (typeof window === "undefined") return;
    const body = document.body;
    if (!body) {
      window.print();
      return;
    }

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      body.classList.remove("print-only-profile", "print-profile");
      window.removeEventListener("afterprint", cleanup);
    };

    window.addEventListener("afterprint", cleanup);
    body.classList.add("print-only-profile", "print-profile");
    window.print();
  };

  // Удаление документа анкеты: подтверждение → запрос → запись в журнал → список.
  const handleDelete = async () => {
    const confirmed = window.confirm(
      pick(
        L(
          "Удалить эту анкету? Действие нельзя отменить.",
          "Delete this questionnaire? This cannot be undone."
        ),
        lang
      )
    );
    if (!confirmed) return;

    setDeleting(true);
    setSaveError("");
    try {
      const response = await GetData({
        module: "patient",
        block: "patient_docs",
        action: "delete",
        id_patient: patientId,
        id: docId,
      });

      if (response?.status !== "success") {
        throw new Error(
          response?.message ||
            pick(L("Не удалось удалить анкету", "Failed to delete questionnaire"), lang)
        );
      }

      try {
        await logPatientChange({
          idPatient: patientId,
          idItem: docId,
          text: buildPatientLogText("patient_docs_deleted", {
            details: pick(L("Анкета (упрощённая)", "Simplified questionnaire"), lang),
          }),
        });
      } catch (_) {
        // Журнал не должен блокировать удаление.
      }

      navigate(`/patient/${patientId}/profile`);
    } catch (err) {
      setSaveError(
        err.message ||
          pick(L("Не удалось удалить анкету", "Failed to delete questionnaire"), lang)
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleGenerateLink = async () => {
    try {
      const response = await GetData({
        module: "anketa",
        block: "link",
        action: "create",
        id_doc: docId,
        id_patient: patientId,
      });

      const path = response?.result?.path;
      if (response?.status === "fail" || !path) {
        throw new Error(response?.message || "Не удалось создать ссылку");
      }

      const fullUrl = `${window.location.origin}${path}`;
      setLink(fullUrl);
      setCopied(false);
      try {
        await navigator.clipboard.writeText(fullUrl);
        setCopied(true);
      } catch (_) {
        // Буфер обмена недоступен — ссылку можно скопировать вручную.
      }
    } catch (err) {
      setLink("");
      setCopied(false);
      setLinkError(err.message || "Не удалось создать ссылку");
    }
  };

  if (loading) {
    return (
      <div className="anketa-page">
        <div style={{ padding: "24px 0", color: "#6b7280" }}>
          {pick(L("Загрузка…", "Loading…"), lang)}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="anketa-page">
        <div className="anketa-link-error">{loadError}</div>
      </div>
    );
  }

  return (
    <div className={`anketa-page${isEditing ? "" : " anketa-page--view"}`}>
      {/* Заголовок и управление */}
      <div className="block-title anketa-header">
        <div className="block-title-text">
          {pick(L("Анкета (упрощённая)", "Simplified questionnaire"), lang)}
        </div>
        <div className="block-title-btn">
          <SegmentedControl
            ariaLabel="Language"
            items={[
              { value: "ru", label: "RU" },
              { value: "en", label: "EN" },
            ]}
            onChange={setLang}
            value={lang}
          />
          {isEditing ? (
            <>
              <Button
                disabled={saving}
                icon="save"
                onClick={handleSave}
                variant="primary"
              >
                {saving
                  ? pick(L("Сохранение…", "Saving…"), lang)
                  : pick(L("Сохранить", "Save"), lang)}
              </Button>
              <Button disabled={saving} onClick={handleCancel} variant="secondary">
                {pick(L("Отмена", "Cancel"), lang)}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleGenerateLink} variant="secondary">
                {pick(L("Сформировать ссылку", "Generate link"), lang)}
              </Button>
              <Button icon="edit" onClick={handleEdit} variant="secondary">
                {pick(L("Редактировать", "Edit"), lang)}
              </Button>
              <IconButton
                icon="print"
                onClick={handlePrint}
                title={pick(L("Печать", "Print"), lang)}
              />
              <IconButton
                disabled={deleting}
                icon="trash"
                onClick={handleDelete}
                title={pick(L("Удалить", "Delete"), lang)}
                variant="danger"
              />
              <IconButton
                icon="back"
                onClick={() => navigate(`/patient/${patientId}/profile`)}
                title={pick(L("Назад к списку", "Back to list"), lang)}
                variant="primary"
              />
            </>
          )}
        </div>
      </div>

      {/* Сформированная ссылка */}
      {link ? (
        <div className="anketa-link-box">
          <span className="anketa-link-label">
            {pick(L("Ссылка на заполнение анкеты", "Questionnaire link"), lang)}:
          </span>
          <input className="anketa-link-input" type="text" readOnly value={link} />
          <span className="anketa-link-status">
            {copied
              ? pick(L("Скопировано", "Copied"), lang)
              : pick(L("Скопируйте вручную", "Copy manually"), lang)}
          </span>
        </div>
      ) : null}

      {/* Ошибка формирования ссылки */}
      {linkError ? (
        <div className="anketa-link-error">{linkError}</div>
      ) : null}

      {/* Ошибка сохранения (остаёмся в режиме редактирования) */}
      {saveError ? <div className="anketa-link-error">{saveError}</div> : null}

      {/* Статус сохранения */}
      {saved ? (
        <div className="anketa-link-status">
          {pick(L("Анкета сохранена", "Questionnaire saved"), lang)}
        </div>
      ) : null}

      {/* Вводная информация (только в режиме редактирования) */}
      {isEditing ? (
        <div className="anketa-intro">
          <div className="anketa-subtitle">
            {pick(L("Упрощённый опросник пациента", "Simplified patient questionnaire"), lang)}
          </div>
          <p className="anketa-intro-text">
            {pick(
              L(
                "Пожалуйста, внимательно ответьте на вопросы о состоянии вашего здоровья. Если вопрос к вам не относится, отметьте «нет». Данная информация необходима для достижения оптимального результата от процедур и предупреждения побочных эффектов и осложнений. Информация конфиденциальна.",
                "Please answer the questions about your health carefully. If a question does not apply to you, mark 'no'. This information is necessary to achieve the optimal result of the procedures and to prevent side effects and complications. The information is confidential."
              ),
              lang
            )}
          </p>
        </div>
      ) : null}

      {/* Разделы анкеты */}
      <div className={`anketa-sections${isEditing ? "" : " anketa-sections--doc"}`}>
        {SIMPLE_ANKETA_SECTIONS.map((section) => (
          <section className="anketa-section" key={section.id}>
            <h3 className="anketa-section-title">
              <span className="anketa-section-num">{section.num}.</span>{" "}
              {pick(section.title, lang)}
            </h3>
            {section.note ? <p className="anketa-section-note">{pick(section.note, lang)}</p> : null}
            <div className="anketa-section-body">
              {section.fields.map((field) => (
                <React.Fragment key={field.id}>
                  {renderField(field, lang, values, handlers, !isEditing)}
                </React.Fragment>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export default PatientAnketaSimple;
